import React from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text, TextLink } from '@frontend/design-system';

type HistoryPaginatorProps = {
  hasNextPage: boolean; // Indicates if there are more pages to fetch
  isLoading: boolean; // Indicates if data is currently being loaded
  onLoadMore: () => void; // Callback to load more data
  visiblePeriod: { start: dayjs.Dayjs; end: dayjs.Dayjs }; // The visible time period
  canLoadMore: boolean; // Indicates if more data can be loaded
  isCurrentPeriodComplete: boolean; // Indicates if the current period is fully loaded
  hasLoadedPreviousPeriods: boolean; // Indicates if previous periods have been loaded
  trackingId?: string; //tracking ID for load more button
};

/**
 * The `HistoryPaginator` component handles pagination for the history panel.
 * It displays appropriate messages and a "Load More" button based on the data state.
 */
export const HistoryPaginator: React.FC<HistoryPaginatorProps> = ({
  hasNextPage,
  isLoading,
  onLoadMore,
  visiblePeriod,
  canLoadMore,
  isCurrentPeriodComplete,
  hasLoadedPreviousPeriods,
  trackingId,
}) => {
  const { t } = useTranslation('contacts');

  const getMessage = (): string | null => {
    if (hasNextPage) return null;

    if (!canLoadMore) {
      return t('🎉 You have reached the end');
    }

    if (isCurrentPeriodComplete && !hasLoadedPreviousPeriods) {
      return t('Showing last 12 months of history');
    }

    if (isCurrentPeriodComplete && hasLoadedPreviousPeriods) {
      return t(`Showing history for {{startDate}} - {{endDate}}`, {
        startDate: visiblePeriod.start.format('MMM D, YYYY'),
        endDate: visiblePeriod.end.format('MMM D, YYYY'),
      });
    }

    return null;
  };

  const message = getMessage();

  if (!message) return null;

  return (
    <div css={styles.wrapper}>
      <Text size='medium' color='subdued'>
        {message}
      </Text>
      {canLoadMore && (
        <TextLink size='medium' weight='bold' onClick={onLoadMore} disabled={isLoading} trackingId={trackingId}>
          {t('Load More')}
        </TextLink>
      )}
    </div>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing(0.5)};
  `,
};
